import { z } from 'zod';
import { dateSchema } from '@/model/utils';

export enum UserRole {
  Admin = 'ADMIN',
  Student = 'STUDENT',
  Instructor = 'INSTRUCTOR',
  SchoolGroupAdmin = 'SCHOOL_GROUP_ADMIN',
  SchoolAdmin = 'SCHOOL_ADMIN',
}

export enum UserAvatarType {
  Default = 'DEFAULT',
  Cat = 'CAT',
  Penguin = 'PENGUIN',
  Frog = 'FROG',
  Rabbit = 'RABBIT',
  Monkey = 'MONKEY',
  Panda = 'PANDA',
}

/**
 * Grade 1 - 12, and college (13)
 */
export enum UserGrade {
  One = 1,
  Two = 2,
  Three = 3,
  Four = 4,
  Five = 5,
  Six = 6,
  Seven = 7,
  Eight = 8,
  Nine = 9,
  Ten = 10,
  Eleven = 11,
  Twelve = 12,
  Thirteen = 13,
  NA = 99,
}

const loginMethodLinkStateSchema = z.object({
  linked: z.boolean(),
  permanent: z.boolean(),
});

const loginMethodsSchema = z.object({
  email: loginMethodLinkStateSchema.optional(),
  google: loginMethodLinkStateSchema.optional(),
  accessCode: z.object({ enabled: z.boolean() }).optional(),
});

export type UserLoginMethods = z.infer<typeof loginMethodsSchema>;

export class User {
  constructor(
    public id: string,
    public firstname: string,
    public lastname: string,
    public classroomIds: string[],
    public schoolId: string,
    public grade: UserGrade,
    public role: UserRole,
    public prefix: string,
    public avatarType?: UserAvatarType | undefined,
    public avatarUrl?: string | undefined,
    /**
     * Only instructors will have email.
     * NOTE: Old instructors won't have email.
     */
    public email?: string | undefined,
    public accessCode?: string | undefined,
    public schoolGroupId?: string,
    public createdAt?: Date,
    public updatedAt?: Date,
    public loginMethods?: UserLoginMethods
  ) {
    this.id = id;
    this.firstname = firstname;
    this.lastname = lastname;
    this.classroomIds = classroomIds;
    this.schoolId = schoolId;
    this.grade = grade;
    this.role = role;
    this.prefix = prefix;
    this.email = email;
    this.accessCode = accessCode;
    this.schoolGroupId = schoolGroupId;
    this.avatarType = avatarType;
    this.avatarUrl = avatarUrl;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.loginMethods = loginMethods;
  }

  public static zodSchema = z.object({
    id: z.string(),
    firstname: z.string(),
    lastname: z.string(),
    classroomIds: z.array(z.string()),
    schoolId: z.string(),
    grade: z.nativeEnum(UserGrade),
    role: z.nativeEnum(UserRole),
    prefix: z.string(),
    avatarType: z.nativeEnum(UserAvatarType).optional(),
    avatarUrl: z.string().optional(),
    email: z.string().email().optional(),
    accessCode: z.string().optional(),
    schoolGroupId: z.string().optional(),
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
    loginMethods: loginMethodsSchema.optional(),
  });

  public static fromJson(json: unknown): User {
    const validJson = this.check(json);
    const user = new User(
      validJson.id,
      validJson.firstname,
      validJson.lastname,
      validJson.classroomIds,
      validJson.schoolId,
      validJson.grade,
      validJson.role,
      validJson.prefix,
      validJson.avatarType,
      validJson.avatarUrl,
      validJson.email,
      validJson.accessCode,
      validJson.schoolGroupId,
      validJson.createdAt,
      validJson.updatedAt,
      validJson.loginMethods
    );
    return user;
  }

  public static fromJsonArray(jsonArray: unknown): User[] {
    const validJsonArray = this.checkArray(jsonArray);
    return validJsonArray.map((json) => this.fromJson(json));
  }

  public static check(json: unknown): z.infer<typeof this.zodSchema> {
    return this.zodSchema.parse(json);
  }

  public static checkArray(jsonArray: unknown): z.infer<typeof this.zodSchema>[] {
    return z.array(this.zodSchema).parse(jsonArray);
  }
}

export class UserAvatar {
  constructor(
    public avatarType: UserAvatarType,
    public avatarUrl: string
  ) {
    this.avatarType = avatarType;
    this.avatarUrl = avatarUrl;
  }

  public static zodSchema = z.object({
    avatarType: z.nativeEnum(UserAvatarType),
    avatarUrl: z.string(),
  });

  public static fromJson(json: unknown): UserAvatar {
    const validJson = this.check(json);
    const userAvatar = new UserAvatar(validJson.avatarType, validJson.avatarUrl);
    return userAvatar;
  }

  public static fromJsonArray(jsonArray: unknown): UserAvatar[] {
    const validJsonArray = this.checkArray(jsonArray);
    return validJsonArray.map((json) => this.fromJson(json));
  }

  public static check(json: unknown): z.infer<typeof this.zodSchema> {
    return this.zodSchema.parse(json);
  }

  public static checkArray(jsonArray: unknown): z.infer<typeof this.zodSchema>[] {
    return z.array(this.zodSchema).parse(jsonArray);
  }
}
